import { Box, Grid, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Button, Typography, IconButton, Tooltip as MuiTooltip } from '@material-ui/core';
import { Print } from '@material-ui/icons';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import React, { useRef, useState } from 'react';
import { Bar, BarChart, CartesianGrid, Cell, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#ed213d', '#b070d6'];

interface VencidaBarAndTableProps {
    data: {
        dias: number;
        ocorrencias: number;
        protocolo: string[];
    }[];
    title?: string;
    name?: string;
}

export default function VencidaBarAndTable({ data, title = '', ...props }: VencidaBarAndTableProps) {
    const [showProtocols, setShowProtocols] = useState(false);
    const printRef = useRef<HTMLDivElement>(null)

    const onPrint = async () => {
        if (!printRef.current) return;

        try {
            const element = printRef.current;
            const canvas = await html2canvas(element, {
                scale: 2,
                useCORS: true,
                logging: false,
            });

            const imgData = canvas.toDataURL('image/png');
            const pdf = new jsPDF({
                orientation: canvas.width > canvas.height ? 'landscape' : 'portrait',
                unit: 'px',
                format: [canvas.width, canvas.height]
            });

            pdf.addImage(imgData, 'PNG', 0, 0, canvas.width, canvas.height);
            pdf.save(`${title || 'relatorio'}.pdf`);
        } catch (error) {
            console.error('Erro ao gerar PDF:', error);
        }
    }

    if (!data) return null;
    return (
        <Box mt={2}>
            <Box display="flex" alignItems="center" flexDirection="row" justifyContent="space-between">
                <Typography variant="h3">{title}</Typography>
                <Box display="flex" alignItems="center" flexDirection="row">
                    <MuiTooltip title={"Imprimir Gráfico"}>
                        <IconButton onClick={() => onPrint()}>
                            <Print />
                        </IconButton>
                    </MuiTooltip>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={() => setShowProtocols((prev) => !prev)}
                    >
                        {showProtocols ? 'Ocultar Protocolos' : 'Mostrar Protocolos'}
                    </Button>
                </Box>
            </Box>
            <h3>{title}</h3>
            <div ref={printRef}>
                <Grid container spacing={1} alignItems="center" justifyContent="center">
                    <Grid item xs={6}>
                        <ResponsiveContainer width="100%" height={500}>
                            <BarChart width={200} height={200} data={data} layout="vertical">
                                <Tooltip />
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis type="number" />
                                <YAxis dataKey="dias" type="category" width={200} />
                                <Bar dataKey="ocorrencias">
                                    {data.map((_entry, index) => (
                                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                    ))}
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                    </Grid>
                    <Grid item xs={6}>
                        <TableContainer component={Paper}>
                            <Table size="small" aria-label="a dense table">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Dias após prazo</TableCell>
                                        <TableCell align="right">Quantidade</TableCell>
                                        <TableCell>Protocolos</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {data.map(row => (
                                        <TableRow key={row.dias}>
                                            <TableCell component="th" scope="row">
                                                {row.dias} Dias
                                            </TableCell>
                                            <TableCell align="right">{row.ocorrencias} Ocorrências</TableCell>
                                            <TableCell align="right">
                                                {showProtocols
                                                    ? row.protocolo.join(', ')
                                                    : '---'}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                    <TableRow>
                                        <TableCell component="th" scope="row">
                                            <b>Total</b>
                                        </TableCell>
                                        <TableCell align="right">
                                            <b>{data.reduce((partial, item) => partial + item.ocorrencias, 0)}</b>
                                        </TableCell>
                                    </TableRow>
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Grid>
                </Grid>
            </div>
        </Box>
    );
}
